import React, { useCallback, useEffect, useRef, useState } from "react";
import { AsyncSelect } from "@caelor/ui";
import { components } from "@atlaskit/select";
import EditorCloseIcon from "@atlaskit/icon/glyph/editor/close";
import { FieldContainer, IconContainer, Label } from "./styled";
import { PickerOptionTypes } from "./option-types";
import { StyledHeader, StyledLabel } from "./StyledHelpers";
import { useTheme } from "styled-components";
import { getCollections } from "../../../../../../../api/cosmos-api";
import { findUsersByName } from "../../../../../../../api/people-api";
import { groupService } from "@Shared/Service/GroupService";
import { useTranslation } from "react-i18next";

function UserGroupCollectionPicker({
  onSelect,
  onCancel,
  selectedUser,
  checkIsUserInStructure,
  noLabel = false,
}) {
  const ref = useRef();
  const theme = useTheme();
  const [reloadKey, setReloadKey] = useState(0);
  const { t } = useTranslation();

  const { DropdownIndicator } = components;

  const mapUserWithStyledLabel = useCallback(
    (user) => ({
      label: <StyledLabel type={PickerOptionTypes.USER} data={user} />,
      value: user.accountId,
      type: PickerOptionTypes.USER,
      name: user.displayName,
    }),
    []
  );

  const mapGroupWithStyledLabel = useCallback(
    (group) => ({
      label: <StyledLabel type={PickerOptionTypes.GROUP} data={group} />,
      value: group.id,
      type: PickerOptionTypes.GROUP,
      name: group.name,
    }),
    []
  );

  const mapCollectionWithStyledLabel = useCallback(
    (collection) => ({
      label: (
        <StyledLabel type={PickerOptionTypes.COLLECTION} data={collection} />
      ),
      value: collection.id,
      type: PickerOptionTypes.COLLECTION,
      name: collection.name,
      filters: collection?.filters || [],
      userGroup: collection?.userGroup || "",
    }),
    []
  );

  const getLoadResults = async (input) => {
    try {
      const userRequest = await findUsersByName(input);
      const data = JSON.parse(userRequest.body);
      const userResults = data.results.filter(
        ({ user }) => user.accountType === "atlassian"
      );

      const groupRequest = await groupService.getGroupsByName(input);
      const { data: group } = groupRequest;

      const formattedGroups = group?.results;

      const collectionsRequest = await getCollections({ queryKey: undefined });

      const { data: collectionResults } = collectionsRequest || { data: [] };

      const options = [
        {
          label: <StyledHeader type={PickerOptionTypes.USER} />,
          options: userResults
            .filter(({ user }) => user.accountType === "atlassian")
            .filter(({ user }) => !checkIsUserInStructure(user.accountId))
            .map(({ user }) => mapUserWithStyledLabel(user)),
        },
        {
          label: <StyledHeader type={PickerOptionTypes.COLLECTION} />,
          options: collectionResults.map((collection) =>
            mapCollectionWithStyledLabel(collection)
          ),
        },
        {
          label: <StyledHeader type={PickerOptionTypes.GROUP} />,
          options: formattedGroups.map((group) =>
            mapGroupWithStyledLabel(group)
          ),
        },
      ];

      return options;
    } catch (error) {
      return [];
    }
  };

  const CustomDropdownIcon = (props) => {
    return (
      <DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen && (
          <IconContainer onClick={() => onCancel()}>
            <EditorCloseIcon primaryColor="#6b778c" />
          </IconContainer>
        )}
      </DropdownIndicator>
    );
  };

  const focusSelect = () => {
    if (ref?.current?.focus) {
      ref.current.focus();
    }
  };

  const triggerReload = () => {
    setReloadKey((prev) => prev + 1);
    setTimeout(() => {
      focusSelect();
    }, 150);
  };

  useEffect(() => {
    focusSelect();
  }, [ref]);

  return (
    <FieldContainer style={{ marginTop: "10px", marginBottom: "10px" }}>
      <Label>{t("people-confluence-groups-and-collections")}</Label>
      {!noLabel && (
        <Label>
          {t("only-add")}
          {selectedUser?.name
            ? ` ${t("below")} ${selectedUser.name}`
            : ` ${t("to-root-level")}`}
        </Label>
      )}
      <AsyncSelect
        ref={ref}
        key={reloadKey}
        value={[]}
        defaultOptions
        placeholder={t("select")}
        isSearchable
        controlShouldRenderValue={false}
        onChange={(option) => {
          onSelect(option);
          triggerReload();
        }}
        defaultMenuIsOpen={true}
        components={{ DropdownIndicator: CustomDropdownIcon }}
        theme={theme}
        loadOptions={getLoadResults}
        closeMenuOnSelect={false}
        openMenuOnFocus
      />
    </FieldContainer>
  );
}

export default UserGroupCollectionPicker;
